import { useEffect, useState } from 'react';
import { Button, Checkbox, Divider } from 'antd';
import { SyncOutlined, SettingOutlined, CalendarOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useToasts } from 'react-toast-notifications';
import {
  saveTimetable,
  TimeSlotsConfiguration,
  TimetableAcademicCalendarModal,
  TimetableActionsProps,
} from '@modules/timetable';
import { useMst } from '@models/Root';
import { colors } from '@theme/index';
import moment from 'moment';
import { DatePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';

export const TimetableActions = observer(
  ({ academicCalendarItems, batchSubjectConfigurations, subjectsData }: TimetableActionsProps) => {
    const [academicCalendarModalVisible, setAcademicCalendarModalVisible] = useState(false);
    const [publish, setPublish] = useState(false);
    const {
      timetable: {
        setInitialTimetableSlotCounts,
        week,
        setWeek,
        resetTimelineItems,
        timelineItems,
        batch,
        updateBatchClassBatchSubjectConfigurations,
        updateBatchClassBatchSubjectExtraSlots,
        removedTimelineItems,
        loadTimeSlotConfigurationFromDB,
        loadTimelineItemsFromDB,
      },
      user: { id: userId },
    } = useMst();
    const [timeSlotsConfigurationVisible, setTimeSlotsConfigurationVisible] = useState<boolean>(false);
    const [isTimetableSaving, setIsTimetableSaving] = useState<boolean>(false);
    const [datePickerPlaceholder, setDatePickerPlaceholder] = useState<string>('Select Week');
    const { addToast } = useToasts();
    const convertToMoment = (date: Date) => {
      if (date) {
        setWeek(moment().year(date?.getFullYear())?.isoWeek(moment(date).isoWeek()));
        setDatePickerPlaceholder(`${date.getFullYear()} - W${moment(date).isoWeek()}`);
      }
    };
    useEffect(() => {
      if (batch && week) {
        const batchClasses = batch.batch_classes.map((batchClass) => batchClass.id);
        const havePublishedItem = timelineItems.find(
          (timelineItem) => batchClasses.includes(timelineItem.batchClassID?.split('/')[1]) && timelineItem.published,
        );

        if (havePublishedItem) {
          setPublish(true);
        } else {
          setPublish(false);
        }
      }
    }, [timelineItems.length, batch?.id, week]);

    return (
      <>
        <DatePicker
          className="timetable-week"
          isoWeek
          showWeekNumbers
          style={{ width: 200 }}
          onChange={convertToMoment}
          placeholder={`${datePickerPlaceholder}`}
          cleanable
          value={null}
          oneTap
        />
        <Button
          id="timetable-academic-calendar"
          icon={
            <CalendarOutlined
              style={{
                color: colors.iconPrimaryColor,
              }}
            />
          }
          onClick={() => {
            setAcademicCalendarModalVisible(true);
          }}
          style={{
            backgroundColor: colors.primaryColor,
            borderColor: colors.iconPrimaryColor,
          }}
        />

        <Button
          id="timetable-configuration"
          type="primary"
          icon={<SettingOutlined />}
          title="Configure Timetable"
          style={{
            backgroundColor: colors.safeButtonColor,
            borderColor: colors.iconPrimaryColor,
          }}
          onClick={() => {
            setTimeSlotsConfigurationVisible(true);
          }}
        />

        <Button
          id="timetable-reset"
          type="primary"
          icon={<SyncOutlined />}
          danger
          title="Reset Timetable"
          onClick={() => {
            resetTimelineItems();
          }}
        />
        <Divider type="vertical" />

        <Checkbox
          id="timetable-publish-check"
          checked={publish}
          onChange={(event) => {
            setPublish(event.target.checked);
          }}
          style={{ marginLeft: 0 }}
          className="publish-check"
        >
          Publish
        </Checkbox>

        <Button
          id="timetable-save"
          type="primary"
          ghost
          loading={isTimetableSaving}
          onClick={() =>
            saveTimetable({
              timelineItems,
              week,
              batchId: batch?.id,
              batchSubjectConfigurations,
              setIsTimetableSaving,
              addToast,
              classIds: batch?.batch_classes.map((batchClass) => batchClass.id) || [],
              publish,
              setInitialTimetableSlotCounts,
              subjectsData,
              updateBatchClassBatchSubjectConfigurations,
              updateBatchClassBatchSubjectExtraSlots,
              removedTimelineItems: removedTimelineItems.map((removedItem) => removedItem),
              userId,
            }).then(() => {
              loadTimelineItemsFromDB();
              loadTimeSlotConfigurationFromDB();
            })
          }
        >
          Save
        </Button>

        <TimeSlotsConfiguration
          timeSlotsConfigurationVisible={timeSlotsConfigurationVisible}
          setTimeSlotsConfigurationVisible={setTimeSlotsConfigurationVisible}
        />
        <TimetableAcademicCalendarModal
          academicCalendarItems={academicCalendarItems}
          academicCalendarModalVisible={academicCalendarModalVisible}
          setAcademicCalendarModalVisible={setAcademicCalendarModalVisible}
        />
      </>
    );
  },
);
